import React, { useState, useEffect, useCallback } from 'react';
import { trustService, caseService, projectService } from '../../services/api';
import { X, DollarSign, AlertCircle, Check } from 'lucide-react';
import useModal from '../../hooks/useModal';
import SearchableSelect from '../common/SearchableSelect';

const NewTransactionModal = ({ isOpen, onClose, trustAccountId, clientId, onTransactionCreated }) => {
  const [formData, setFormData] = useState({
    transaction_type: 'deposit',
    amount: '',
    description: '',
    reference_number: '',
    case_id: '',
    project_id: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cases, setCases] = useState([]);
  const [projects, setProjects] = useState([]);

  // Modal behavior
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(isOpen, onClose);

  const fetchCasesAndProjects = useCallback(async () => {
    try {
      // Fetch cases for this client
      const casesResponse = await caseService.getCasesByClient(clientId);
      setCases(casesResponse.data || []);

      // Fetch all projects for these cases
      if (casesResponse.data && casesResponse.data.length > 0) {
        const allProjects = [];
        for (const caseItem of casesResponse.data) {
          try {
            const projectsResponse = await projectService.getProjectsByCase(caseItem.id);
            if (projectsResponse.data) {
              // Add case_id to each project for filtering
              const projectsWithCaseId = projectsResponse.data.map(project => ({
                ...project,
                case_id: caseItem.id
              }));
              allProjects.push(...projectsWithCaseId);
            }
          } catch (error) {
            console.error(`Error fetching projects for case ${caseItem.id}:`, error);
          }
        }
        setProjects(allProjects);
      }
    } catch (error) {
      console.error('Error fetching cases and projects:', error);
      setError('Failed to load available cases');
    }
  }, [clientId]);

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setFormData({
        transaction_type: 'deposit',
        amount: '',
        description: '',
        reference_number: '',
        case_id: '',
        project_id: '',
        notes: ''
      });
      setError('');

      // Fetch available cases and projects for this client
      if (clientId) {
        fetchCasesAndProjects();
      }
    }
  }, [isOpen, clientId, fetchCasesAndProjects]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate required fields
      if (!formData.amount || !formData.description) {
        setError('Amount and description are required');
        return;
      }

      // Convert amount to proper format
      let amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount === 0) {
        setError('Please enter a valid amount');
        return;
      }

      // Make withdrawals negative
      if (['withdrawal', 'fee_earned', 'refund', 'transfer'].includes(formData.transaction_type) && amount > 0) {
        amount = -amount;
      }

      const transactionData = {
        trust_account_id: trustAccountId,
        transaction_type: formData.transaction_type,
        amount: amount,
        description: formData.description,
        reference_number: formData.reference_number || null,
        case_id: formData.case_id || null,
        project_id: formData.project_id || null,
        notes: formData.notes || null
      };

      const newTransaction = await trustService.createTransaction(transactionData);
      
      // Call the callback to refresh the parent component
      if (onTransactionCreated) {
        onTransactionCreated(newTransaction);
      }
      
      onClose();
    } catch (error) {
      console.error('Error creating transaction:', error);
      setError(error.response?.data?.detail || 'Failed to create transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      
      // Clear project_id when case_id changes
      if (name === 'case_id') {
        newData.project_id = '';
      }
      
      return newData;
    });
  };

  const getTransactionTypeLabel = (type) => {
    const labels = {
      deposit: 'Deposit',
      withdrawal: 'Withdrawal',
      transfer: 'Transfer',
      interest: 'Interest',
      refund: 'Refund',
      adjustment: 'Adjustment',
      fee_earned: 'Fee Earned'
    };
    return labels[type] || type;
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4 z-[9999]"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div className="fixed inset-x-0 top-16 bottom-0 bg-black bg-opacity-60 -z-10"></div>
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <DollarSign className="h-6 w-6 text-yellow-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">New Trust Transaction</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {/* Transaction Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Transaction Type *
            </label>
            <select
              name="transaction_type"
              value={formData.transaction_type}
              onChange={handleInputChange}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="deposit">💰 Deposit - Money into trust</option>
              <option value="withdrawal">💸 Withdrawal - Money from trust</option>
              <option value="fee_earned">💼 Fee Earned - Convert trust to earned fees</option>
              <option value="transfer">↔️ Transfer - Between accounts/cases</option>
              <option value="interest">📈 Interest - IOLTA interest earned</option>
              <option value="refund">🔄 Refund - Return to client</option>
              <option value="adjustment">⚖️ Adjustment - Manual correction</option>
            </select>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                onWheel={(e) => e.target.blur()}
                step="0.01"
                min="0"
                required
                placeholder="0.00"
                className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {['withdrawal', 'fee_earned', 'refund'].includes(formData.transaction_type) 
                ? 'Enter positive amount - will be automatically converted to negative'
                : 'Enter positive amount for credits to the account'
              }
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <input
              type="text"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              maxLength={500}
              placeholder="Brief description of the transaction"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            />
          </div>

          {/* Reference Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reference Number
            </label>
            <input
              type="text"
              name="reference_number"
              value={formData.reference_number}
              onChange={handleInputChange}
              maxLength={100}
              placeholder="Check number, wire ID, invoice number, etc."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            />
          </div>

          {/* Case Association */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Associated Case (Optional)
            </label>
            <SearchableSelect
              value={formData.case_id}
              onChange={(val) => {
                setFormData(prev => ({
                  ...prev,
                  case_id: val,
                  project_id: '' // Clear project when case changes
                }));
              }}
              options={cases.map(case_item => ({
                value: case_item.id,
                label: `${case_item.case_number} - ${case_item.title}`
              }))}
              emptyOption="Select a case (optional)"
            />
          </div>

          {/* Project Association */}
          {formData.case_id && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Associated Project (Optional)
              </label>
              <SearchableSelect
                value={formData.project_id}
                onChange={(val) => setFormData(prev => ({ ...prev, project_id: val }))}
                options={projects
                  .filter(project => project.case_id === formData.case_id)
                  .map(project => ({
                    value: project.id,
                    label: project.name
                  }))}
                emptyOption="Select a project (optional)"
              />
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              maxLength={1000}
              placeholder="Any additional details or notes about this transaction..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            />
          </div>

          {/* Transaction Preview */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Transaction Preview</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <div>Type: {getTransactionTypeLabel(formData.transaction_type)}</div>
              {formData.amount && (
                <div className={`font-medium ${
                  ['withdrawal', 'fee_earned', 'refund'].includes(formData.transaction_type)
                    ? 'text-red-600'
                    : 'text-green-600'
                }`}>
                  Amount: {['withdrawal', 'fee_earned', 'refund'].includes(formData.transaction_type) ? '-' : '+'}
                  {trustService.formatCurrency(Math.abs(parseFloat(formData.amount) || 0))}
                </div>
              )}
              {formData.description && <div>Description: {formData.description}</div>}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-yellow-500 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.amount || !formData.description}
              className="px-4 py-2 text-sm font-medium text-white bg-yellow-500 border border-transparent rounded-lg hover:bg-yellow-600 focus:ring-2 focus:ring-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Create Transaction
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewTransactionModal;